import React, { useEffect } from 'react';
import { BasicModal } from '@shared/Widgets/Modals/BasicModal/BasicModal';
import css from './MapModal.module.css';
import { GoogleMap } from '@shared/Widgets/GoogleMap/GoogleMap';
import { MapMarkerIcon } from '@shared/Icons/MapMarkerIcon';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import ThinArrowIcon from '@shared/Icons/ThinArrowIcon';
import GoogleMapSuggestionsInput from '@shared/Ui/CustomInputs/GoogleMapSuggestionsInput/GoogleMapSuggestionsInput';
import { useGoogleMapViewModel } from '@viewModels/GoogleMap/GoogleMapViewModel';
import { useMap } from '@vis.gl/react-google-maps';
import { useMediaType } from '@shared/Hooks/useMediaTypeHook';
import { GOOGLE_MAP_ID } from '@shared/Constants/GoogleMap/GoogleMapId';

interface Props {
    onClose: () => void;
}

export const MapModal: React.FC<Props> = ({ onClose }) => {
    const {
        markerPosition,
        setAddress,
        getAddressSuggestions,
        setAddressSuggestions,
        setMarkerPosition,
        isWithinCountry,
        geocodeAddress,
        address,
        addressSuggestions,
    } = useGoogleMapViewModel();

    const [_isDesktop, _isTablet, isMobile] = useMediaType();

    const map = useMap(GOOGLE_MAP_ID);

    useEffect(() => {
        if (map && markerPosition) {
            map.setCenter({ lat: markerPosition.lat, lng: markerPosition.lng });
            map.setZoom(10);
        }
    }, [map, markerPosition]);

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAddress(value);
        getAddressSuggestions(value);
    };

    const handleAddressSelect = async (suggestion: string) => {
        setAddress(suggestion);
        setAddressSuggestions([]);

        const location = await geocodeAddress(suggestion);
        if (location) {
            const isValidLocation = await isWithinCountry(
                location.lat,
                location.lng
            );
            if (isValidLocation) {
                setMarkerPosition(location);
            } else {
                setMarkerPosition(null);
            }
        } else {
            setMarkerPosition(null);
        }
    };

    const handleConfirmAddress = async () => {
        if (address && markerPosition) {
            setAddress(address);
            setMarkerPosition(markerPosition);
        }
        onClose();
    };

    const foundAddress = address;
    return (
        <BasicModal onClose={onClose} extraClassName={css.modal}>
            <div className={css.header}>Select your address on the map</div>

            <div className={css.addressContainer}>
                <div className={css.address}>
                    <span className={css.markerIcon}>
                        <MapMarkerIcon
                            fill={'var(--color-interactive-primary)'}
                        />
                    </span>
                    {foundAddress ? (
                        <span>
                            Is{' '}
                            <span className={css.currentAddressValue}>
                                {foundAddress}
                            </span>{' '}
                            your address?
                        </span>
                    ) : (
                        <span>Enter your address or select it on map</span>
                    )}
                </div>
                {!isMobile && (
                    <BasicButton
                        extraClassName={css.btn}
                        onClick={handleConfirmAddress}
                    >
                        <div className={css.btnContent}>
                            Confirm address{' '}
                            <ThinArrowIcon
                                fill={'none'}
                                stroke={'currentColor'}
                                className={css.arrowIcon}
                            />
                        </div>
                    </BasicButton>
                )}
            </div>

            <GoogleMapSuggestionsInput
                value={address}
                onChange={handleAddressChange}
                placeholder={`Enter project address`}
                suggestions={addressSuggestions}
                onSelectSuggestion={handleAddressSelect}
                showSearchIcon={true}
            ></GoogleMapSuggestionsInput>

            {isMobile && (
                <BasicButton
                    extraClassName={css.btn}
                    onClick={handleConfirmAddress}
                >
                    <div className={css.btnContent}>
                        Confirm address{' '}
                        <ThinArrowIcon
                            fill={'none'}
                            stroke={'currentColor'}
                            className={css.arrowIcon}
                        />
                    </div>
                </BasicButton>
            )}

            <div className={css.mapWrapper}>
                <div className={css.map}>
                    <GoogleMap />
                </div>
            </div>
        </BasicModal>
    );
};
